import Avatar from '@/components/common/Avatar';
import EmptyState from '@/components/common/EmptyState';
import Pagination from '@/components/common/Pagination';
import StatusBadge from '@/components/common/StatusBadge';
import TableHeader from '@/components/common/TableHeader';
import SelectInput from '@/components/Forms/SelectInput';
import TextInput from '@/components/Forms/TextInput';
import ThemeWrapper from '@/components/ThemeWrapper';
import useRoutes from '@/hooks/useRoutes';
import useThemeColors from '@/hooks/useThemeColors';
import StaffLayout from '@/layouts/StaffLayout';
import { User } from '@/types';
import { Link, router } from '@inertiajs/react';
import clsx from 'clsx';
import { GraduationCap, Search, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

interface Course {
    id: number;
    title: string;
}

interface Rank {
    id: number;
    name: string;
    points_to_unlock: number;
}

interface Filters {
    search: string;
    min_points: number | null;
    max_points: number | null;
    rank: number | null;
}

interface Pagination {
    page: number;
    per_page: number;
    total: number;
    num_pages: number;
    has_previous: boolean;
    has_next: boolean;
    previous_page: number | null;
    next_page: number | null;
}

interface StudentsIndexProps {
    students: User[];
    courses: Course[];
    ranks: Rank[];
    filters: Filters;
    pagination: Pagination;
}

const IndexContent: React.FC<StudentsIndexProps> = ({
    students,
    courses,
    ranks,
    filters,
    pagination,
}) => {
    const { route } = useRoutes();
    const colors = useThemeColors();

    const [searchQuery, setSearchQuery] = useState(filters.search || '');
    const [minPoints, setMinPoints] = useState(
        filters.min_points !== null && filters.min_points !== undefined
            ? String(filters.min_points)
            : ''
    );
    const [maxPoints, setMaxPoints] = useState(
        filters.max_points !== null && filters.max_points !== undefined
            ? String(filters.max_points)
            : ''
    );
    const [selectedRank, setSelectedRank] = useState<string>(
        filters.rank !== null && filters.rank !== undefined
            ? String(filters.rank)
            : ''
    );
    const [selectedCourse, setSelectedCourse] = useState<string>('');

    // Filter students based on course filter (client-side, current page only)
    // Points/rank/search are handled server-side.
    const filteredStudents = useMemo(() => {
        let filtered = students;

        if (selectedCourse) {
            const courseId = Number(selectedCourse);
            filtered = filtered.filter((student) =>
                student.courses?.some(
                    (course: Course) => course.id === courseId
                )
            );
        }

        return filtered;
    }, [students, selectedCourse]);

    // Build the query-string for the current set of server-side filters.
    const buildParams = (overrides: Record<string, string | number> = {}) => {
        const params: Record<string, string | number> = {};

        if (searchQuery.trim()) params.search = searchQuery.trim();
        if (minPoints.trim()) params.min_points = minPoints.trim();
        if (maxPoints.trim()) params.max_points = maxPoints.trim();
        if (selectedRank) params.rank = selectedRank;

        return { ...params, ...overrides };
    };

    // Detect changes vs server-rendered filters (so we don't push a redundant request on mount).
    const filtersChanged = () => {
        const currentSearch = filters.search || '';
        const currentMin =
            filters.min_points !== null && filters.min_points !== undefined
                ? String(filters.min_points)
                : '';
        const currentMax =
            filters.max_points !== null && filters.max_points !== undefined
                ? String(filters.max_points)
                : '';
        const currentRank =
            filters.rank !== null && filters.rank !== undefined
                ? String(filters.rank)
                : '';

        return (
            searchQuery.trim() !== currentSearch ||
            minPoints.trim() !== currentMin ||
            maxPoints.trim() !== currentMax ||
            selectedRank !== currentRank
        );
    };

    // Debounce filter changes → server roundtrip, always resetting to page 1.
    useEffect(() => {
        const timer = setTimeout(() => {
            if (!filtersChanged()) return;

            router.get(
                route('staff-students').toString(),
                buildParams({ page: 1 }),
                { preserveState: true, preserveScroll: false }
            );
        }, 300);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, minPoints, maxPoints, selectedRank]);

    const handlePageChange = (page: number) => {
        router.get(route('staff-students').toString(), buildParams({ page }), {
            preserveState: true,
            preserveScroll: true,
        });
    };

    const clearAdvancedFilters = () => {
        setMinPoints('');
        setMaxPoints('');
        setSelectedRank('');
    };

    const advancedFiltersActive =
        minPoints.trim() !== '' ||
        maxPoints.trim() !== '' ||
        selectedRank !== '';

    return (
        <StaffLayout>
            <div className="mx-auto max-w-7xl">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        Estudiantes
                    </h1>
                    <p className="mt-2 text-gray-600 dark:text-neutral-300">
                        Gestión de estudiantes de la academia
                    </p>
                </div>

                {/* Search and Filters */}
                <div className="mb-6 space-y-3">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        {/* Search Bar */}
                        <div className="relative flex-1 sm:max-w-md">
                            <Search
                                className="absolute top-1/2 left-3 -translate-y-1/2 transform text-gray-400 dark:text-neutral-500"
                                size={18}
                            />
                            <input
                                type="text"
                                placeholder="Buscar por nombre o email..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className={clsx(
                                    'w-full rounded-md border bg-white py-2 pr-10 pl-10 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:ring-2 focus:outline-none dark:bg-neutral-800 dark:text-white dark:placeholder:text-neutral-500',
                                    `border-gray-300 dark:border-neutral-600 ${colors.inputFocus}`
                                )}
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => setSearchQuery('')}
                                    className="absolute top-1/2 right-3 -translate-y-1/2 transform text-gray-400 hover:text-gray-600 dark:text-neutral-500 dark:hover:text-neutral-300"
                                    aria-label="Limpiar búsqueda"
                                >
                                    <X size={18} />
                                </button>
                            )}
                        </div>

                        {/* Course Filter */}
                        <div className="sm:w-64">
                            <SelectInput
                                name="course"
                                value={selectedCourse}
                                onChange={(v) => setSelectedCourse(v)}
                            >
                                <option value="">Todos los cursos</option>
                                {courses.map((course) => (
                                    <option key={course.id} value={course.id}>
                                        {course.title}
                                    </option>
                                ))}
                            </SelectInput>
                        </div>
                    </div>

                    {/* Advanced filters: points range + rank (scoped to current academy) */}
                    <div className="flex flex-col gap-3 rounded-md border border-gray-200 bg-gray-50 p-3 sm:flex-row sm:items-end sm:gap-4 dark:border-neutral-700 dark:bg-neutral-700">
                        <div className="flex-1">
                            <TextInput
                                label="Puntos mín."
                                name="min_points"
                                type="number"
                                value={minPoints}
                                onChange={(v) => setMinPoints(v)}
                                min={0}
                                placeholder="0"
                            />
                        </div>
                        <div className="flex-1">
                            <TextInput
                                label="Puntos máx."
                                name="max_points"
                                type="number"
                                value={maxPoints}
                                onChange={(v) => setMaxPoints(v)}
                                min={0}
                                placeholder="∞"
                            />
                        </div>
                        <div className="flex-1">
                            <SelectInput
                                label="Rango"
                                name="rank"
                                value={selectedRank}
                                onChange={(v) => setSelectedRank(v)}
                            >
                                <option value="">Todos los rangos</option>
                                {ranks.map((rank) => (
                                    <option key={rank.id} value={rank.id}>
                                        {rank.name} ({rank.points_to_unlock}+
                                        pts)
                                    </option>
                                ))}
                            </SelectInput>
                        </div>
                        {advancedFiltersActive && (
                            <button
                                type="button"
                                onClick={clearAdvancedFilters}
                                className="inline-flex items-center gap-1 self-end rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
                            >
                                <X size={14} />
                                Limpiar filtros
                            </button>
                        )}
                    </div>
                </div>

                {/* Results Count */}
                {pagination.total > 0 && (
                    <div className="mb-4 text-sm text-gray-600 dark:text-neutral-300">
                        Mostrando {filteredStudents.length} de{' '}
                        {pagination.total} estudiantes
                    </div>
                )}

                {students.length === 0 && !searchQuery.trim() ? (
                    <EmptyState
                        icon={GraduationCap}
                        title="No hay estudiantes"
                        description="No hay estudiantes inscritos en los cursos de tu academia."
                    />
                ) : students.length === 0 || filteredStudents.length === 0 ? (
                    <EmptyState
                        icon={Search}
                        title="No se encontraron resultados"
                        description="Intenta ajustar los filtros de búsqueda."
                    />
                ) : (
                    <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-neutral-800">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-700">
                            <TableHeader
                                columns={[
                                    { label: 'Nombre' },
                                    { label: 'Email' },
                                    { label: 'Academia' },
                                    { label: 'Inscripciones' },
                                    { label: 'Puntos' },
                                    { label: 'Rango' },
                                    { label: 'Discord' },
                                    { label: 'Estado' },
                                ]}
                            />
                            <tbody className="divide-y divide-gray-200 bg-white dark:divide-neutral-700 dark:bg-neutral-800">
                                {filteredStudents.map((student) => (
                                    <tr
                                        key={student.id}
                                        className="hover:bg-gray-50 dark:hover:bg-neutral-700"
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <Link
                                                href={route(
                                                    'staff-students-detail',
                                                    { student_id: student.id }
                                                ).toString()}
                                                className="flex items-center"
                                            >
                                                <div className="mr-3">
                                                    <Avatar
                                                        user={student}
                                                        size="md"
                                                    />
                                                </div>
                                                <div className="text-sm font-medium text-gray-900 hover:text-blue-600 dark:text-white">
                                                    {student.full_name}
                                                </div>
                                            </Link>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-500 dark:text-neutral-400">
                                                {student.email}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 dark:text-white">
                                                {student.current_academy
                                                    ?.name || '-'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 dark:text-white">
                                                {student.enrollments_count || 0}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 dark:text-white">
                                                {student.points || 0}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 dark:text-white">
                                                {student.rank?.name || '-'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-500 dark:text-neutral-400">
                                                {student.discord_username ||
                                                    '-'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <StatusBadge
                                                status={
                                                    student.is_active
                                                        ? 'active'
                                                        : 'inactive'
                                                }
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        <Pagination
                            currentPage={pagination.page}
                            totalPages={pagination.num_pages}
                            hasPrevious={pagination.has_previous}
                            hasNext={pagination.has_next}
                            onPrevious={() =>
                                handlePageChange(pagination.previous_page!)
                            }
                            onNext={() =>
                                handlePageChange(pagination.next_page!)
                            }
                        />
                    </div>
                )}
            </div>
        </StaffLayout>
    );
};

const Index: React.FC<StudentsIndexProps> = (props) => {
    return (
        <ThemeWrapper>
            <IndexContent {...props} />
        </ThemeWrapper>
    );
};

export default Index;
